import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import DynamicBreadcrumbs from "../../../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";
import { BreadcrumbContainer, CustomBreadcrumbWrapper } from "../../../components/Breadcrumb/Breadcrumb.styled";
import { useStore } from "../../../../../../hooks";
import { useDrivesStores } from "../../../hook/use-drives.hook";
import { useFolderHierarchy } from "../../../hooks/useFolderHierarchy";

const DriveBreadcrumb = observer(() => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { breadcrumbStore } = useStore();
  const { drivesViewStore, activeFolderStore } = useDrivesStores();
  const { vaultUuid, folderHierarchy, isDriveContext } = useFolderHierarchy();

  const handleNavigate = (href) => {
    if (href && href !== pathname) {
      navigate(href);
    }
  };

  // ✅ PERFORMANCE: Extract primitive values to avoid array/object dependencies
  const drivesLength = drivesViewStore?.drivesStore?.drives?.length ?? 0;
  const folderNamesMapKeys = Object.keys(activeFolderStore.folderNamesMap || {}).join(',');

  // ✅ Get drive name from drivesStore
  const driveName = useMemo(() => {
    if (!vaultUuid || drivesLength === 0) {
      return 'Loading...';
    }
    const drive = drivesViewStore.drivesStore.drives.find(d => d.id === vaultUuid);
    return drive?.title || 'Drive';
  }, [vaultUuid, drivesLength]);

  useEffect(() => {
    if (!isDriveContext || !vaultUuid) {
      return;
    }

    const items = [
      { label: 'AIMS Drive', href: '/v2/drives' },
      { label: driveName, href: `/v2/drives/d/${vaultUuid}` }
    ];

    // ✅ Add folder hierarchy to breadcrumb if there are nested folders
    if (folderHierarchy.length > 0) {
      const folderNamesMap = activeFolderStore.folderNamesMap || {};
      
      folderHierarchy.forEach((folderUuid, index) => {
        // Build cumulative URL: /v2/drives/d/vaultUuid/folder1/folder2/...
        const folderPath = folderHierarchy.slice(0, index + 1).join('/');
        const urlPath = `/v2/drives/d/${vaultUuid}/${folderPath}`;
        const folderName = folderNamesMap[folderUuid] || 'Loading...';
        
        items.push({
          label: folderName,
          href: urlPath
        });
      });
    }

    breadcrumbStore.setItems(items);
    breadcrumbStore.setCurrentPath(pathname);

    return () => {
      breadcrumbStore.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultUuid, driveName, folderHierarchy, folderNamesMapKeys, pathname, isDriveContext]);

  return (
    <BreadcrumbContainer>
      <CustomBreadcrumbWrapper>
        <DynamicBreadcrumbs 
          items={breadcrumbStore.breadcrumbItems} 
          currentPath={breadcrumbStore.currentPath}
          onNavigate={handleNavigate}
        />
      </CustomBreadcrumbWrapper>
    </BreadcrumbContainer>
  );
});

export default DriveBreadcrumb;

